
import { Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { forkJoin } from 'rxjs';
import { DeviceFilterPipe } from 'src/core/pipes/devicefilter.pipe';
import { HistorySortPipe } from 'src/core/pipes/historysort.pipe';
import { AuthService } from 'src/core/services/auth.service';
import { ChatService } from 'src/core/services/chat.service';
import { DataService } from 'src/core/services/data.service';
import { HistoryService } from 'src/core/services/history.service';
import { LoadingService } from 'src/core/services/loading.service';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { WidgetService } from 'src/core/services/widget.service';
import { ApiResponse } from 'src/core/types/general.types';
import { NONE_DEVICE_SPECIFIC, NONE_DEVICE_SPECIFIC_TRANSLATION_KEY } from 'src/core/types/chat.types';
import { ExactSystem, SelectedSystem } from 'src/core/types/selectedSystem.types';

@Component({
  selector: 'app-chathistory',
  imports: [DeviceFilterPipe, HistorySortPipe, TranslocoModule],
  templateUrl: './chathistory.component.html',
  styleUrl: './chathistory.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChathistoryComponent {

  chatHistory;
  uniqueDevices = [];
  deleteModalOpen: boolean = false;
  toBeDeletedChatId: string = '';
  skippingBoolean: boolean = false;
  isDeleting: boolean = false;
  isError: boolean = false;
  isLoading: boolean = true;
  expanded: Map<string, boolean> = new Map();
  readonly noneDeviceSpecific = NONE_DEVICE_SPECIFIC;
  readonly noneDeviceSpecificTranslationKey = NONE_DEVICE_SPECIFIC_TRANSLATION_KEY;

  constructor(
    private historyService: HistoryService,
    private chatService: ChatService,
    private selectedSystemService: SelectedsystemService,
    private widgetService: WidgetService,
    private authService: AuthService,
    private dataService: DataService,
    private loadingService: LoadingService,
    private selectedContentService: SelectedContentService
  ) { }

  ngOnInit(): void {

    //Triggering when adding new stuff to local Storage
    this.historyService.history.subscribe(() => {

      this.isLoading = true;

      //Get History os user
      this.historyService.retrieveChatHistory().subscribe({
        next: (data) => {

          this.chatHistory = data;

          if (data.length > 0) {
            let uniques = this.getDevicesForChats(data);
            this.uniqueDevices = uniques;

            this.expanded = new Map(uniques.map(device => [device.serial, false]));


          }
          else {
            this.uniqueDevices = []
          };

          this.isLoading = false;
        },
        error: (err) => {
          console.error("Error retrieving chat history:", err);
          this.chatHistory = [];
          this.isError = true;
          this.isLoading = false;
        }
      }
      );

    });

  }

  openChat(conversation) {
    if (this.chatService.isStreaminginProgress()) return;
    //We need to check is the chat selected is for another instrument
    this.historyService.getSingleChat(conversation).subscribe({
      next: (data) => {

        //Check if we need to change the active device 
        if ((this.selectedContentService.currentSystem?.SerialNumber !== data[0].serial
          || this.selectedContentService.currentSystem?.MaterialNumber !== data[0].material)
          && data[0].serial !== NONE_DEVICE_SPECIFIC
        ) {

          this.selectedSystemService.setNewDevice(data[0].material, data[0].serial).subscribe(
            {
              next: (newdevice) => {
                console.log("Selected Device after opening chat:", newdevice);
              }
            }
          );
        }

        else if (data[0].serial === NONE_DEVICE_SPECIFIC) {

          this.selectedContentService.clear();

        }

  this.chatService.messages.next(this.chatService.localizeMessageMetadata(data.sort((a, b) => a.number - b.number)));
        this.widgetService.$workareaSize.next("small");
      },
      error: (err) => {
        console.error("Error retrieving single chat:", err)
      }
    });
  }


  getDevicesForChats(chats: any[]): any[] {

    let uniqueDevices = new Map<string, {}>();

    for (const chat of chats) {
      uniqueDevices.set(chat.serial + chat.material, { serial: chat.serial, material: chat.material });
    }

    return Array.from(uniqueDevices.values());
  }

  cropChat(chat: string) {

    if (chat && chat?.length > 80) {
      return chat.substring(0, 80) + "...";
    }
    return chat;
  }

  markForDelete(chatId: string, event) {

    event.stopPropagation();

    this.toBeDeletedChatId = chatId;

    //Check if asking is skipped
    if (localStorage.getItem("LSCOskipDeleteConfirmation") === "true") {
      this.deleteChat();
      return;
    }

    this.deleteModalOpen = true;
  }

  closeModal() {
    this.deleteModalOpen = false;
    this.toBeDeletedChatId = '';
  }

  setConfirmation(event) {
    this.skippingBoolean = event.target.active;
  }

  deleteChat() {

    if (!this.toBeDeletedChatId) {
      console.error("No chat ID provided for deletion.");
      return;
    }
    console.log("Deleting chat with ID:", this.toBeDeletedChatId);

    this.isDeleting = true;

    //Close if chat is the active one
    if (this.chatService.messages.value.length > 0 && this.chatService.messages.value[0].chatId === this.toBeDeletedChatId) {
      this.chatService.messages.next([]);
      this.widgetService.$workareaSize.next("small");
    }

    forkJoin([
      this.chatService.deleteChat(this.toBeDeletedChatId, "V2"),
      this.historyService.deleteChat({ accountId: this.authService.userData.basic["id"], chatId: this.toBeDeletedChatId })
    ]).subscribe({
      next: () => {
        console.log("Chat deleted successfully");

        //Apply setting to local storage
        localStorage.setItem('LSCOskipDeleteConfirmation', this.skippingBoolean ? 'true' : 'false');
        this.deleteModalOpen = false;

        //Update CChat History Pane on the left
        this.historyService.history.next();

        this.isDeleting = false;

      },
      error: (err) => {
        console.error("Error deleting chat:", err);
        this.isDeleting = false;
      }
    });

  }

  expandHistory(deviceId: string, expanded) {
    this.expanded.set(deviceId, expanded);
  }

  checkIfExpandable(serial) {
    return this.chatHistory.filter(chat => chat.serial === serial).length > 3;
  }

  checkIfDupSerial(serialNumber) {
    return this.uniqueDevices.filter(item => item["serial"] === serialNumber).length > 1;
  }

}
